import React, {memo, useCallback, useMemo} from 'react'
import {FormattedMessage} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getLanguageCode} from 'modules/meta/selectors'
import {getTrustpilotBottomSheetVisibility} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'

import {BottomSheet} from '@vio/atlas/src/components/designSystem/BottomSheet'
import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {NoStyleLink} from '@vio/atlas/src/components/designSystem/helpers/NoStyleLink'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {cssTheme} from '@vio/atlas/src/themes'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {
  useGetBusinessUnitQuery,
  useGetReviewsQuery
} from '@vio/core/src/trustpilotWidget/services/trustpilotApi'
import {TrustpilotWidget} from '@vio/shared/src/search/TrustpilotWidget'
import {TRUSTPILOT_REVIEWS_URL} from '@vio/shared/src/search/TrustpilotWidget/constants'

import {TRUSTPILOT_LABELS} from '../constants'
import {TrustpilotReviewItem} from '../TrustpilotReviewItem'

const WidgetWrapper = styled(NoStyleLink)`
  display: block;
  border-width: ${cssTheme.layout.borderWidth.bw150};
  border-style: solid;
  border-color: ${cssTheme.colors.border.neutral.secondary};
  padding: ${cssTheme.layout.spacing.s400};
  border-radius: ${cssTheme.layout.radius.lg};
  margin-bottom: ${cssTheme.layout.spacing.s500};
`

const EMPTY_REVIEWS: never[] = []

const SNAP_POINTS = {top: 'nearFull', middle: 0.8} as const

const TrustpilotBottomSheetComponent = () => {
  const dispatch = useDispatch()
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const {shell} = useBrandCapabilities()

  const isOpen = useSelector(getTrustpilotBottomSheetVisibility)
  const languageCode = useSelector(getLanguageCode)
  const isFeatureDisabled = (!isMobile && !isDesktopXs) || !shell?.hasTrustpilot
  const {data, isLoading} = useGetBusinessUnitQuery(undefined, {
    skip: !shell.hasTrustpilot
  })

  const {data: reviewsData} = useGetReviewsQuery(
    {
      language: languageCode
    },
    {
      skip: isFeatureDisabled
    }
  )

  const reviews = reviewsData?.reviews ?? EMPTY_REVIEWS

  const handleClickViewAllReviews = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.TrustpilotAllReviewsButton,
        action: Action.Clicked
      })
    )
  }, [dispatch])

  const handleClickWidget = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.TrustpilotWidget,
        action: Action.Clicked,
        payload: {
          source: 'TrustpilotBottomSheet'
        }
      })
    )
  }, [dispatch])

  const onClose = useCallback(() => {
    dispatch(hideOverlay())
  }, [dispatch])

  const navBarProps = useMemo(
    () => ({
      centerContent: (
        <FormattedMessage {...TRUSTPILOT_LABELS.travelersTrustVio} />
      )
    }),
    []
  )

  const bottomBarContent = useMemo(
    () => (
      <Button
        fullWidth
        iconEnd={<Icon name="Share" size="sm" />}
        variant="tertiary"
        onClick={handleClickViewAllReviews}
        href={TRUSTPILOT_REVIEWS_URL}
        target="_blank"
      >
        <FormattedMessage {...TRUSTPILOT_LABELS.viewAllReviews} />
      </Button>
    ),
    [handleClickViewAllReviews]
  )

  if (isFeatureDisabled) return null

  return (
    <BottomSheet
      isOpen={isOpen}
      navBarProps={navBarProps}
      onClose={onClose}
      noPaddings
      snapPoints={SNAP_POINTS}
      bottomBarContent={bottomBarContent}
    >
      <Box padding="s500">
        <WidgetWrapper
          href={TRUSTPILOT_REVIEWS_URL}
          target="_blank"
          onClick={handleClickWidget}
        >
          <TrustpilotWidget
            isLoading={isLoading}
            variant="panel"
            numberOfReviews={data?.numberOfReviews?.total}
            stars={data?.score?.stars}
            trustScore={data?.score?.trustScore}
          />
        </WidgetWrapper>
        {reviews.map((review, index) => (
          <TrustpilotReviewItem
            key={review.id}
            review={review}
            hasDivider={index < reviews.length - 1}
          />
        ))}
      </Box>
    </BottomSheet>
  )
}

export const TrustpilotBottomSheet = memo(TrustpilotBottomSheetComponent)
